import { useState, useCallback } from 'react';
import * as dateFns from 'date-fns';

// Shared "Invoice Date" range-picker state for the seller/organization invoice
// lists. Keeps the date-object state local (not URL-synced) and exposes the
// handlers + formatted (yyyy-MM-dd) range the list passes to the API.
const buildDefaultRange = () => [
  {
    endDate: new Date(),
    startDate: dateFns.addDays(new Date(), -6),
    key: 'selection',
  },
];

const useInvoiceDateRange = (setPage) => {
  const [dateRange, setDateRange] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [tempDateRange, setTempDateRange] = useState(buildDefaultRange());
  const [calendarFilterTitle, setCalendarFilterTitle] = useState('Select Date');
  const [tempCalendarFilterTitle, setTempCalendarFilterTitle] =
    useState('Last 7 days');

  const handleSelect = (ranges) => {
    const staticRanges = [
      {
        label: 'Today',
        range: () => ({ startDate: new Date(), endDate: new Date() }),
      },
      {
        label: 'Last 7 days',
        range: () => ({
          startDate: dateFns.addDays(new Date(), -6),
          endDate: new Date(),
        }),
      },
      {
        label: 'Last 30 days',
        range: () => ({
          startDate: dateFns.addDays(new Date(), -29),
          endDate: new Date(),
        }),
      },
    ];

    const selectedRange = staticRanges.find(
      (r) =>
        r.range().startDate.getTime() ===
          ranges.selection.startDate.getTime() &&
        r.range().endDate.getTime() === ranges.selection.endDate.getTime(),
    );

    setTempCalendarFilterTitle(selectedRange?.label || 'Custom');
    setTempDateRange([ranges.selection]);
  };

  const handleApply = () => {
    setPage(1);
    setDateRange(tempDateRange);
    setCalendarFilterTitle(tempCalendarFilterTitle);
    setShowCalendar(false);
  };

  const resetDateRange = () => {
    setTempDateRange(buildDefaultRange());
    setDateRange(null);
    setCalendarFilterTitle('Select Date');
    setTempCalendarFilterTitle('Last 7 days');
    setShowCalendar(false);
  };

  // Stable across renders (only changes when the applied range changes) so
  // callers can safely list it as a hook dependency without re-fetch loops.
  const getFormattedDates = useCallback(() => {
    if (!dateRange || !dateRange[0]) {
      return { start: null, end: null };
    }
    return {
      start: dateFns.format(dateRange[0].startDate, 'yyyy-MM-dd'),
      end: dateFns.format(dateRange[0].endDate, 'yyyy-MM-dd'),
    };
  }, [dateRange]);

  return {
    dateRange,
    showCalendar,
    setShowCalendar,
    tempDateRange,
    calendarFilterTitle,
    handleSelect,
    handleApply,
    handleClear: resetDateRange,
    resetDateRange,
    getFormattedDates,
  };
};

export default useInvoiceDateRange;
